import React, { useEffect, useMemo, useState } from 'react';
import { PaymentMethod, PaymentSplit, Transaction } from '../types';
import { usePos } from '../context/PosContext';
import { printReceiptAuto, maybePrintCoupon } from '../services/receipt';
import { upiConfigured, upiUri } from '../services/upi';
import { generateQrMatrix } from '../services/qrcode';
import {
  getLoyaltySettings,
  pointsEarned,
  redeemError,
  redeemValue,
} from '../services/loyalty';
import './PaymentModal.css';

interface Props {
  transaction: Transaction;
  // Records the sale and returns the finalized transaction (with its committed
  // sequential bill number), so the receipt prints the real number.
  onComplete: (transaction: Transaction) => Transaction | void | Promise<Transaction | void>;
  onCancel: () => void;
}

const METHODS: { key: PaymentMethod; label: string }[] = [
  { key: 'cash', label: 'Cash' },
  { key: 'card', label: 'Card' },
  { key: 'digital', label: 'Digital' },
  { key: 'credit', label: 'Credit' },
];

const round2 = (n: number) => Math.round(n * 100) / 100;

const PaymentModal: React.FC<Props> = ({ transaction, onComplete, onCancel }) => {
  const { config, formatMoney, customers, isOn } = usePos();

  const customer = customers.find((c) => c.id === transaction.customerId);
  const loyalty = useMemo(() => getLoyaltySettings(), []);
  const loyaltyOn = isOn('enable_loyalty_points') && loyalty.enabled && !!customer;
  const availablePoints = customer?.loyaltyPoints ?? 0;

  // Seed from the bill so the customer / payment mode chosen on the POS screen
  // carry through (and aren't overwritten with empty defaults on completion).
  const [customerName, setCustomerName] = useState<string>(transaction.customerName ?? '');

  // --- Counter discount (applied at payment time, on top of any bill discount) ---
  const [discInput, setDiscInput] = useState<string>('');
  const [discMode, setDiscMode] = useState<'amt' | 'pct'>('amt');
  const manualDiscount = useMemo(() => {
    const n = parseFloat(discInput) || 0;
    if (n <= 0) return 0;
    const amt = discMode === 'pct' ? (transaction.total * n) / 100 : n;
    return round2(Math.min(amt, transaction.total));
  }, [discInput, discMode, transaction.total]);
  // Bill total after the counter discount, before loyalty.
  const afterDiscount = round2(Math.max(0, transaction.total - manualDiscount));

  // --- Loyalty redemption ---
  const [redeemInput, setRedeemInput] = useState<string>('');
  const redeemPts = Math.max(0, Math.floor(parseFloat(redeemInput) || 0));
  const redeemErr = loyaltyOn ? redeemError(redeemPts, availablePoints, loyalty) : null;
  const loyaltyDiscount = useMemo(() => {
    if (!loyaltyOn || redeemErr || redeemPts <= 0) return 0;
    return round2(Math.min(redeemValue(redeemPts, loyalty), afterDiscount));
  }, [loyaltyOn, redeemErr, redeemPts, loyalty, afterDiscount]);

  // Amount the customer must still pay after the discount and loyalty redemption.
  const due = round2(Math.max(0, afterDiscount - loyaltyDiscount));

  // --- Tender ---
  const [splitMode, setSplitMode] = useState(false);
  // "Keyboard Setting / B - Keyboard Enter Payment Mode Digital" preselects the
  // digital tender so Enter completes a digital payment.
  const [method, setMethod] = useState<PaymentMethod>(
    isOn('keyboard_payment_mode_digital') ? 'digital' : transaction.paymentMethod ?? 'cash'
  );
  const [cashGiven, setCashGiven] = useState<string>(due.toFixed(2));
  const [splitAmt, setSplitAmt] = useState<Record<PaymentMethod, string>>({
    cash: '', card: '', digital: '', credit: '',
  });

  // Keep Cash Received in step with the amount due as the discount / loyalty
  // redemption changes. A manual edit to Cash Received doesn't change `due`, so
  // this effect doesn't fire and won't clobber the cashier's own entry.
  useEffect(() => {
    setCashGiven(due.toFixed(2));
  }, [due]);

  const cashNum = parseFloat(cashGiven || '0');
  const splitTotal = round2(
    METHODS.reduce((s, m) => s + (parseFloat(splitAmt[m.key] || '0') || 0), 0)
  );
  const remaining = round2(due - (splitMode ? splitTotal : due));

  const creditWithoutCustomer = (m: PaymentMethod) => m === 'credit' && !customer;

  // Whether the Complete button can fire.
  const canComplete = (() => {
    if (due <= 0 && loyaltyDiscount > 0) return true; // fully covered by points
    if (splitMode) {
      if (Math.abs(splitTotal - due) > 0.01) return false;
      if ((parseFloat(splitAmt.credit || '0') || 0) > 0 && !customer) return false;
      return true;
    }
    if (creditWithoutCustomer(method)) return false;
    if (method === 'cash') return cashNum >= due - 0.01;
    return true;
  })();

  const change = method === 'cash' && !splitMode ? Math.max(0, round2(cashNum - due)) : 0;

  const buildPayments = (m: PaymentMethod = method): PaymentSplit[] => {
    if (due <= 0) return [];
    if (splitMode) {
      return METHODS.map((x) => ({ method: x.key, amount: round2(parseFloat(splitAmt[x.key] || '0') || 0) }))
        .filter((p) => p.amount > 0);
    }
    return [{ method: m, amount: due }];
  };

  // Dominant tender (largest split, else the single method) for receipts/reports.
  const dominantMethod = (payments: PaymentSplit[]): PaymentMethod => {
    if (payments.length === 0) return method;
    return payments.reduce((a, b) => (b.amount > a.amount ? b : a)).method;
  };

  const buildCompletedTransaction = (m: PaymentMethod = method): Transaction => {
    const payments = buildPayments(m);
    const loyaltyEarned = loyaltyOn ? pointsEarned(due, loyalty) : 0;
    const cashPaid = payments.filter((p) => p.method === 'cash').reduce((s, p) => s + p.amount, 0);
    return {
      ...transaction,
      paymentMethod: dominantMethod(payments),
      payments: payments.length > 1 ? payments : undefined,
      // Fold the counter discount into the bill's discount total.
      discount: round2(transaction.discount + manualDiscount),
      total: due,
      amountPaid: splitMode ? splitTotal : m === 'cash' ? cashNum : due,
      change: !splitMode && m === 'cash' ? change : 0,
      loyaltyDiscount: loyaltyDiscount > 0 ? loyaltyDiscount : undefined,
      loyaltyRedeemed: loyaltyDiscount > 0 ? redeemPts : undefined,
      loyaltyEarned: loyaltyEarned > 0 ? loyaltyEarned : undefined,
      customerName: customerName.trim() || undefined,
      status: 'completed',
    };
  };

  // `print=false` mirrors the Android Save Bill button (record without printing).
  // `m` overrides the tender (the UPI Save / UPI Print quick buttons pay digital).
  const handlePayment = async (print = true, m: PaymentMethod = method) => {
    if (m === method && !canComplete) return;
    const completed = buildCompletedTransaction(m);
    // Record the sale FIRST so a printing problem can never lose it, then print
    // the finalized sale (with its committed sequential bill number).
    const finalized = await onComplete(completed);
    const recorded = finalized || completed;
    if (print) printReceipt(recorded);
    // "8 — Coupon Setting": fire the coupon slip on save / on print.
    maybePrintCoupon(recorded, config, print ? 'print' : 'save');
  };

  // Print a receipt formatted per the saved Print Settings. Best-effort: a print
  // failure is logged but never thrown (so it can't block recording the sale).
  const printReceipt = (tx: Transaction = buildCompletedTransaction()) => {
    printReceiptAuto(tx, config).catch((error) => {
      console.error('Receipt printing failed:', error);
    });
  };

  const setSplit = (m: PaymentMethod, v: string) =>
    setSplitAmt((prev) => ({ ...prev, [m]: v }));

  // Quick-fill a split row with whatever is still outstanding.
  const fillRemaining = (m: PaymentMethod) => {
    const others = METHODS.filter((x) => x.key !== m).reduce(
      (s, x) => s + (parseFloat(splitAmt[x.key] || '0') || 0),
      0
    );
    setSplit(m, round2(Math.max(0, due - others)).toFixed(2));
  };

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal">
        <div className="modal-header">
          <h2>{splitMode ? 'Split Payment' : 'Payment'}</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <div className="modal-content">
          <div className="transaction-summary">
            <h3>Order Summary</h3>
            <div className="summary-line">
              <span>Subtotal:</span>
              <span>{formatMoney(transaction.subtotal)}</span>
            </div>
            <div className="summary-line">
              <span>Tax:</span>
              <span>{formatMoney(transaction.tax)}</span>
            </div>
            {manualDiscount > 0 && (
              <div className="summary-line discount">
                <span>Discount{discMode === 'pct' ? ` (${parseFloat(discInput) || 0}%)` : ''}:</span>
                <span>− {formatMoney(manualDiscount)}</span>
              </div>
            )}
            {loyaltyDiscount > 0 && (
              <div className="summary-line loyalty">
                <span>Loyalty ({redeemPts} pts):</span>
                <span>− {formatMoney(loyaltyDiscount)}</span>
              </div>
            )}
            <div className="summary-line total">
              <span>Amount Due:</span>
              <span>{formatMoney(due)}</span>
            </div>
          </div>

          <div className="discount-section">
            <div className="discount-head">
              <span>Discount</span>
              {manualDiscount > 0 && (
                <button className="link-btn" onClick={() => setDiscInput('')}>clear</button>
              )}
            </div>
            <div className="discount-row">
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="0"
                value={discInput}
                onChange={(e) => setDiscInput(e.target.value)}
              />
              <div className="discount-toggle">
                <button
                  className={discMode === 'amt' ? 'active' : ''}
                  onClick={() => setDiscMode('amt')}
                >
                  {config.currencySymbol}
                </button>
                <button
                  className={discMode === 'pct' ? 'active' : ''}
                  onClick={() => setDiscMode('pct')}
                >
                  %
                </button>
              </div>
              {manualDiscount > 0 && (
                <span className="discount-val">− {formatMoney(manualDiscount)}</span>
              )}
            </div>
          </div>

          <div className="customer-info">
            <label>
              Customer Name (Optional):
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter customer name"
              />
            </label>
          </div>

          {loyaltyOn && (
            <div className="loyalty-section">
              <div className="loyalty-head">
                <span>Loyalty Points</span>
                <span className="loyalty-balance">{availablePoints} available</span>
              </div>
              <div className="loyalty-redeem">
                <input
                  type="number"
                  min={0}
                  placeholder="Redeem points"
                  value={redeemInput}
                  onChange={(e) => setRedeemInput(e.target.value)}
                />
                {redeemInput && (
                  <button className="link-btn" onClick={() => setRedeemInput('')}>clear</button>
                )}
                {loyaltyDiscount > 0 && (
                  <span className="loyalty-val">= {formatMoney(loyaltyDiscount)}</span>
                )}
              </div>
              {redeemErr && <div className="loyalty-err">{redeemErr}</div>}
            </div>
          )}

          <div className="payment-methods">
            <div className="pm-head">
              <h3>Payment Method</h3>
              <label className="split-toggle">
                <input
                  type="checkbox"
                  checked={splitMode}
                  onChange={(e) => setSplitMode(e.target.checked)}
                />
                Split
              </label>
            </div>

            {!splitMode ? (
              <div className="payment-options">
                {METHODS.map((m) => (
                  <label
                    key={m.key}
                    className={`payment-option ${creditWithoutCustomer(m.key) ? 'disabled' : ''}`}
                    title={creditWithoutCustomer(m.key) ? 'Link a customer to pay on credit' : undefined}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={m.key}
                      checked={method === m.key}
                      disabled={creditWithoutCustomer(m.key)}
                      onChange={() => setMethod(m.key)}
                    />
                    <span className="method-label">{m.label}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="split-rows">
                {METHODS.map((m) => (
                  <div className="split-row" key={m.key}>
                    <span className="split-label">{m.label}</span>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="0.00"
                      value={splitAmt[m.key]}
                      disabled={creditWithoutCustomer(m.key)}
                      onChange={(e) => setSplit(m.key, e.target.value)}
                    />
                    <button
                      className="link-btn"
                      disabled={creditWithoutCustomer(m.key)}
                      onClick={() => fillRemaining(m.key)}
                    >
                      rest
                    </button>
                  </div>
                ))}
                <div className={`split-balance ${Math.abs(remaining) < 0.01 ? 'ok' : ''}`}>
                  <span>{remaining > 0.01 ? 'Remaining' : remaining < -0.01 ? 'Over' : 'Balanced'}</span>
                  <span>{formatMoney(Math.abs(remaining))}</span>
                </div>
              </div>
            )}
          </div>

          {!splitMode && method === 'cash' && (
            <div className="amount-section">
              <label>
                Cash Received:
                <input
                  type="number"
                  step="0.01"
                  min={due}
                  value={cashGiven}
                  onChange={(e) => setCashGiven(e.target.value)}
                />
              </label>
              {change > 0 && <div className="change-amount">Change: {formatMoney(change)}</div>}
            </div>
          )}

          {/* Scan-to-pay UPI QR (Android "UPI Print" / UPI Setting) — shown for
              a digital tender when a UPI VPA is configured in Settings. */}
          {!splitMode && method === 'digital' && upiConfigured(config) && due > 0 && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <UpiQr uri={upiUri(config, due, `Bill ${transaction.billNo}`)} />
              <div style={{ fontSize: 13, color: '#555' }}>
                Scan to pay {formatMoney(due)} → {config.upiId}
              </div>
            </div>
          )}

          {!splitMode && method === 'credit' && customer && (
            <div className="credit-note">
              {formatMoney(due)} will be added to {customer.name}'s account
              {(customer.creditBalance ?? 0) > 0 && (
                <> (current balance {formatMoney(customer.creditBalance ?? 0)})</>
              )}.
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="secondary-btn" onClick={() => printReceipt()}>
            Print Receipt
          </button>
          {/* UPI Setting "A - UPI Print Button" / "B - UPI Save Button": one-tap
              digital (UPI) settlement, with or without printing the bill. */}
          {!splitMode && upiConfigured(config) && isOn('upi_save_button') && (
            <button className="secondary-btn" onClick={() => handlePayment(false, 'digital')}>
              UPI Save
            </button>
          )}
          {!splitMode && upiConfigured(config) && isOn('upi_button') && (
            <button className="complete-btn" onClick={() => handlePayment(true, 'digital')}>
              UPI Print
            </button>
          )}
          <button className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          {/* Save without print (Android Save Bill vs Print Bill buttons). The
              disable_save/print_button app settings hide the matching action. */}
          {!isOn('disable_save_button') && (
            <button className="secondary-btn" onClick={() => handlePayment(false)} disabled={!canComplete}>
              Complete (No Print)
            </button>
          )}
          {!isOn('disable_print_button') && (
            <button className="complete-btn" onClick={() => handlePayment(true)} disabled={!canComplete}>
              Complete &amp; Print
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline SVG QR for the UPI deep link (same generator as QR Login).
const UpiQr: React.FC<{ uri: string }> = ({ uri }) => {
  const matrix = useMemo(() => generateQrMatrix(uri, 'M'), [uri]);
  const n = matrix.length;
  const quiet = 2;
  const total = n + quiet * 2;
  return (
    <svg
      width={150}
      height={150}
      viewBox={`0 0 ${total} ${total}`}
      shapeRendering="crispEdges"
      style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 6 }}
    >
      <rect width={total} height={total} fill="#fff" />
      {matrix.map((row, y) =>
        row.map((on, x) =>
          on ? <rect key={`${x}-${y}`} x={x + quiet} y={y + quiet} width={1} height={1} fill="#000" /> : null
        )
      )}
    </svg>
  );
};

export default PaymentModal;
